import { useEffect, useState } from "react";
import { api, money, slotLabel, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { DollarSign, CalendarCheck, Users, Video, Save, Wallet } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { CampsManager, PayoutsPanel } from "@/components/CoachTools";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TIMES = ["06:00", "07:30", "15:30", "16:00", "17:00", "18:00", "19:00", "20:00"];

export default function CoachDashboard() {
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("bookings");
  const [completing, setCompleting] = useState(null);
  const [note, setNote] = useState("");
  const [drills, setDrills] = useState("");
  const [library, setLibrary] = useState([]);

  const load = () => {
    api.get("/coach/me").then((r) => setProfile(r.data)).catch(() => {});
    api.get("/coach/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/coach/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/coach/drills").then((r) => setLibrary(r.data.drills)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveProfile = async () => {
    try {
      const { data } = await api.put("/coaches/me", {
        category: profile.category, city: profile.city, region: profile.region, headline: profile.headline, bio: profile.bio,
        experience_years: Number(profile.experience_years), hourly_from: Number(profile.hourly_from),
        levels: profile.levels, specialties: profile.specialties, weekly_availability: profile.weekly_availability,
      });
      setProfile(data);
      toast.success("Profile updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleSlot = (entry) =>
    setProfile((p) => ({ ...p, weekly_availability: p.weekly_availability.includes(entry)
      ? p.weekly_availability.filter((x) => x !== entry) : [...p.weekly_availability, entry] }));

  const markComplete = async () => {
    try {
      await api.post(`/coach/bookings/${completing.id}/complete`, {
        coach_note: note,
        drills: drills.split(",").map((d) => d.trim()).filter(Boolean),
      });
      toast.success("Session logged — the family can see your notes");
      setCompleting(null); setNote(""); setDrills("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const coachCancel = async (id) => {
    try {
      const { data } = await api.post(`/coach/bookings/${id}/cancel`, {});
      toast.success(`Cancelled — $${data.refunded.toFixed(2)} refunded to the client`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!profile) return <div className="p-20 text-center text-zinc-500">Loading dashboard…</div>;

  const input = "mt-1.5 w-full bg-black border border-white/10 text-sm px-3 py-2.5 focus:border-[#00e5ff] focus:outline-none focus:ring-1 focus:ring-[#00e5ff] transition-colors duration-200";
  const cards = [
    { icon: DollarSign, label: "Gross revenue", value: money(stats?.revenue) },
    { icon: Wallet, label: "Net payout", value: money(stats?.net_payout) },
    { icon: CalendarCheck, label: "Paid bookings", value: stats?.total_bookings ?? 0 },
    { icon: Users, label: "Clients", value: stats?.clients?.length ?? 0 },
    { icon: Video, label: "Video orders", value: stats?.video_orders ?? 0 },
  ];

  return (
    <div data-testid="coach-dashboard-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#ff3b30]">Coach console</span>
          <h1 className="display text-4xl uppercase tracking-tighter font-bold mt-2">{profile.name}</h1>
        </div>
        <div className="flex gap-2">
          {["bookings", "camps", "clients", "payouts", "profile"].map((t) => (
            <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
              className={`text-[11px] uppercase tracking-widest px-4 py-2.5 border transition-colors duration-200 ${
                tab === t ? "border-[#00e5ff] text-[#00e5ff]" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map(({ icon: Icon, label, value }) => (
          <div key={label} data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="border border-white/10 bg-[#121212] p-5 h-full">
            <Icon className="w-4 h-4 text-[#00e5ff]" />
            <p className="display text-3xl font-bold mt-3 leading-none">{value}</p>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2">{label}</p>
            {label === "Net payout" && (
              <p className="text-[10px] text-zinc-600 mt-1">after {stats?.coach_fee_percent ?? 2.5}% platform fee</p>
            )}
          </div>
        ))}
      </div>

      <div data-testid="payout-chart" className="mt-4 border border-white/10 bg-[#121212] p-6">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-4">Net payout by month (after platform fee)</p>
        {stats?.revenue_by_month?.length > 0 ? (
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.revenue_by_month}>
                <XAxis dataKey="month" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a" }} />
                <Bar dataKey="revenue" fill="#00e5ff" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-xs text-zinc-500">No paid bookings yet — your payouts will chart here.</p>
        )}
      </div>

      {tab === "bookings" && (
        <div className="mt-8 space-y-3">
          {bookings.length === 0 && <p data-testid="coach-no-bookings" className="border border-white/10 p-10 text-sm text-zinc-500">No bookings yet.</p>}
          {bookings.map((b) => (
            <div key={b.id} data-testid={`coach-booking-${b.id}`} className="border border-white/10 bg-[#121212] p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
              <div className="md:col-span-3">
                <p className="display uppercase font-bold text-lg leading-none">{b.user_name}</p>
                <p className="text-[11px] text-zinc-500 mt-1">{b.user_email}</p>
              </div>
              <div className="md:col-span-3 text-xs text-zinc-300">
                {b.service_title}
                {b.athlete_name && <span className="block text-[10px] text-zinc-500 mt-1">Goalie: {b.athlete_name}</span>}
              </div>
              <div className="md:col-span-2 text-xs text-zinc-400">{b.kind === "video" ? (b.video_url || "film link pending") : slotLabel(b.slot)}</div>
              <div className="md:col-span-2 text-sm font-semibold text-[#00e5ff]">
                {money(b.coach_payout ?? b.amount)}
                <span className="block text-[10px] text-zinc-500 font-normal">{money(b.amount)} gross · {b.payment_status}</span>
              </div>
              <div className="md:col-span-2 md:text-right space-y-2">
                {b.status === "confirmed" ? (
                  <>
                    <button data-testid={`complete-booking-${b.id}`} onClick={() => setCompleting(b)}
                      className="text-[10px] uppercase tracking-widest border border-[#00e5ff] text-[#00e5ff] px-3 py-2 hover:bg-[#00e5ff] hover:text-black transition-colors duration-200">
                      Mark complete
                    </button>
                    <button data-testid={`coach-cancel-${b.id}`} onClick={() => coachCancel(b.id)}
                      className="block md:ml-auto text-[10px] uppercase tracking-widest border border-white/15 text-zinc-400 px-3 py-2 hover:border-[#ff3b30] hover:text-[#ff3b30] transition-colors duration-200">
                      Cancel & refund
                    </button>
                  </>
                ) : (
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">{b.status.replace("_", " ")}</span>
                )}
                {b.notes && <p className="text-[11px] text-zinc-500 mt-2">"{b.notes}"</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "camps" && <CampsManager />}
      {tab === "payouts" && <PayoutsPanel />}

      {tab === "clients" && (
        <div className="mt-8 border border-white/10 divide-y divide-white/10">
          {(stats?.clients || []).length === 0 && <p data-testid="coach-no-clients" className="p-10 text-sm text-zinc-500">No paying clients yet.</p>}
          {(stats?.clients || []).map((c) => (
            <div key={c.email} data-testid={`client-row-${c.email}`} className="p-5 flex items-center justify-between bg-[#121212]">
              <span className="text-sm font-semibold">{c.name}</span>
              <span className="text-xs text-zinc-500">{c.email}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "profile" && (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-white/10 bg-[#121212] p-6 space-y-4">
            <h2 className="display text-xl uppercase tracking-tight font-bold">Profile details</h2>
            <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Discipline (changing this resets your service list)</span>
              <select data-testid="profile-category-select" className={input} value={profile.category || "Goalie Coach"}
                onChange={(e) => setProfile({ ...profile, category: e.target.value })}>
                {["Goalie Coach", "Strength & Conditioning", "Mobility Specialist", "Vision & Cognitive", "Mental Performance"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">City</span>
                <input data-testid="profile-city-input" className={input} value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} /></label>
              <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Region</span>
                <input data-testid="profile-region-input" className={input} value={profile.region} onChange={(e) => setProfile({ ...profile, region: e.target.value })} /></label>
              <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Years experience</span>
                <input data-testid="profile-experience-input" type="number" className={input} value={profile.experience_years} onChange={(e) => setProfile({ ...profile, experience_years: e.target.value })} /></label>
              <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Hourly from ($)</span>
                <input data-testid="profile-price-input" type="number" className={input} value={profile.hourly_from} onChange={(e) => setProfile({ ...profile, hourly_from: e.target.value })} /></label>
            </div>
            <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Headline</span>
              <input data-testid="profile-headline-input" className={input} value={profile.headline} onChange={(e) => setProfile({ ...profile, headline: e.target.value })} /></label>
            <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Bio</span>
              <textarea data-testid="profile-bio-input" rows={4} className={input} value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} /></label>
            <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Specialties (comma separated)</span>
              <input data-testid="profile-specialties-input" className={input} value={profile.specialties.join(", ")}
                onChange={(e) => setProfile({ ...profile, specialties: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></label>
            <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Levels (comma separated)</span>
              <input data-testid="profile-levels-input" className={input} value={profile.levels.join(", ")}
                onChange={(e) => setProfile({ ...profile, levels: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></label>
            <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Drill library (comma separated — tap these when completing a session)</span>
              <input data-testid="drill-library-input" className={input} value={library.join(", ")}
                onChange={(e) => setLibrary(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></label>
            <button data-testid="save-drills-btn" onClick={async () => {
              try {
                const { data } = await api.put("/coach/drills", { drills: library });
                setLibrary(data.drills);
                toast.success("Drill library saved");
              } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
            }} className="border border-[#00e5ff] text-[#00e5ff] uppercase tracking-wide text-xs px-6 py-3 hover:bg-[#00e5ff] hover:text-black transition-colors duration-200">
              Save drill library
            </button>
            <button data-testid="save-profile-btn" onClick={saveProfile}
              className="bg-[#00e5ff] text-black font-bold uppercase tracking-wide px-6 py-3 flex items-center gap-2 hover:bg-[#00b3cc] transition-colors duration-200">
              <Save className="w-4 h-4" /> Save profile
            </button>
          </div>

          <div className="border border-white/10 bg-[#121212] p-6">
            <h2 className="display text-xl uppercase tracking-tight font-bold">Weekly availability</h2>
            <p className="text-xs text-zinc-500 mt-2">Selected times repeat every week for the next 3 weeks.</p>
            <div className="mt-6 space-y-3">
              {DAYS.map((d) => (
                <div key={d} className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] uppercase tracking-widest text-zinc-400 w-10">{d}</span>
                  {TIMES.map((t) => {
                    const entry = `${d} ${t}`;
                    const on = profile.weekly_availability.includes(entry);
                    return (
                      <button key={entry} data-testid={`avail-${entry.replace(" ", "-").replace(":", "")}`} onClick={() => toggleSlot(entry)}
                        className={`text-[10px] px-2 py-1.5 border transition-colors duration-200 ${
                          on ? "border-[#00e5ff] bg-[#00e5ff] text-black font-bold" : "border-white/10 text-zinc-500 hover:border-white/40"}`}>
                        {t}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
            <button data-testid="save-availability-btn" onClick={saveProfile}
              className="mt-6 border border-[#00e5ff] text-[#00e5ff] uppercase tracking-wide text-xs px-6 py-3 hover:bg-[#00e5ff] hover:text-black transition-colors duration-200">
              Save availability
            </button>
          </div>
        </div>
      )}

      {completing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-6">
          <div data-testid="complete-modal" className="w-full max-w-md bg-[#121212] border border-white/15 p-8">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Log session</span>
            <h2 className="display text-2xl uppercase tracking-tight font-bold">{completing.athlete_name || completing.user_name}</h2>
            <p className="text-[11px] text-zinc-500 mt-1">{completing.service_title}</p>
            <label className="block mt-5">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Session notes for the family</span>
              <textarea data-testid="complete-note-input" rows={4} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="What you worked on, what improved, what's next…" className={input} />
            </label>
            <label className="block mt-4">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Drills to practise (comma separated)</span>
              <input data-testid="complete-drills-input" value={drills} onChange={(e) => setDrills(e.target.value)}
                placeholder="T-push recovery, post integration, tracking through screens" className={input} />
            </label>
            {library.length > 0 && (
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">Your drill library — tap to add</p>
                <div data-testid="drill-library" className="flex flex-wrap gap-2 mt-2 max-h-32 overflow-y-auto">
                  {library.map((d) => {
                    const active = drills.split(",").map((x) => x.trim().toLowerCase()).includes(d.toLowerCase());
                    return (
                      <button
                        key={d}
                        data-testid={`drill-chip-${d.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => setDrills((prev) => {
                          const list = prev.split(",").map((x) => x.trim()).filter(Boolean);
                          return active ? list.filter((x) => x.toLowerCase() !== d.toLowerCase()).join(", ")
                                        : [...list, d].join(", ");
                        })}
                        className={`text-[10px] px-2.5 py-1.5 border transition-colors duration-200 ${
                          active ? "border-[#00e5ff] bg-[#00e5ff] text-black font-bold" : "border-white/10 text-zinc-400 hover:border-[#00e5ff]"}`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex gap-3 mt-6">
              <button data-testid="complete-submit-btn" onClick={markComplete}
                className="flex-1 bg-[#00e5ff] text-black font-bold uppercase tracking-wide py-3.5 hover:bg-[#00b3cc] transition-colors duration-200">
                Save & complete
              </button>
              <button data-testid="complete-cancel-btn" onClick={() => setCompleting(null)}
                className="px-5 border border-white/15 text-xs uppercase text-zinc-400 hover:border-white/40 transition-colors duration-200">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
